import { useEffect, useRef, useState } from "react";
import { FieldError } from "../ui/feedback";
import { FilterPanel, type FilterPanelHandle } from "../ui/FilterPanel";
import { ChatIcon } from "../ui/icons/Icon";
import { RangeCalendar, dayOf, type Day } from "../ui/RangeCalendar";
import { ChatAvatar } from "./ChatAvatar";
import { TagChipContent } from "./ContactTagBadge";
import { clearedQuery, rowTagLimit, searchThreshold, type ContactOption, type ContactQuery } from "./contactModel";
import type { ContactWorkspaceController, WorkspaceView } from "./contactWorkspaceController";
import { contains, Highlighted } from "./searchText";
import { styleObject, tagStyle } from "./tagColor";

// Filtros de Contactos (ContactFilters.razor del portal actual): la fila de píldoras y «Más filtros» con Etiquetas,
// Bandeja, Agente y Creación del contacto. El panel trabaja sobre un borrador; solo «Aplicar» cambia la consulta.

const NONE = "NONE";
const pressed = (value: boolean) => (value ? "true" : "false");
const lower = (value: string) => value.toLowerCase();

/** Hoy en la zona de la empresa: los días que vienen no se eligen. */
function companyToday(timezone: string): Day {
  try {
    const parts = new Intl.DateTimeFormat("en-CA", {
      timeZone: timezone,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    }).formatToParts(new Date());
    const get = (type: string) => Number(parts.find((part) => part.type === type)?.value);
    return dayOf(get("year"), get("month"), get("day"));
  } catch {
    const now = new Date();
    return dayOf(now.getUTCFullYear(), now.getUTCMonth() + 1, now.getUTCDate());
  }
}

const esMonths = [
  "enero",
  "febrero",
  "marzo",
  "abril",
  "mayo",
  "junio",
  "julio",
  "agosto",
  "septiembre",
  "octubre",
  "noviembre",
  "diciembre",
];
const enMonths = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];
const split = (day: Day) => day.split("-").map(Number) as [number, number, number];

export function ContactFilters({ ctl, view }: { ctl: ContactWorkspaceController; view: WorkspaceView }) {
  const value = view.query;
  // I13/C22 · en la primera carga la fila se arma con lo de la dirección; las opciones llegan con la lista.
  const page = view.page ?? { agents: [], hasUnassigned: false, inboxes: [], tags: [], timezone: "UTC" };
  const locale = ctl.locale;
  const panel = useRef<FilterPanelHandle>(null);
  const [draftTags, setDraftTags] = useState<string[]>([]);
  const [draftAny, setDraftAny] = useState(false);
  const [tagSearch, setTagSearch] = useState("");
  const [options, setOptions] = useState<string[]>([]);
  const [tagsMore, setTagsMore] = useState(false);
  const [tagError, setTagError] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [inbox, setInbox] = useState("");
  const [agent, setAgent] = useState("");
  const [agentSearch, setAgentSearch] = useState("");
  const [agentExpanded, setAgentExpanded] = useState(false);
  const [selField, setSelField] = useState("CREATED");
  const [period, setPeriod] = useState("ANY");
  const [from, setFrom] = useState<Day | null>(null);
  const [to, setTo] = useState<Day | null>(null);
  const [fromText, setFromText] = useState<string | null>(null);
  const [toText, setToText] = useState<string | null>(null);
  const [calendarOpening, setCalendarOpening] = useState(0);
  const tagsPending = useRef<AbortController | null>(null);
  const tagsRevision = useRef(0);
  useEffect(() => () => tagsPending.current?.abort(), []);

  const today = companyToday(page.timezone);
  const datePattern = locale === "en-US" ? "MM/dd/yyyy" : "dd/MM/yyyy";
  const show = (day: Day | null) => {
    if (!day) return "";
    const [year, month, date] = split(day);
    const two = (n: number) => String(n).padStart(2, "0");
    return locale === "en-US" ? `${two(month)}/${two(date)}/${year}` : `${two(date)}/${two(month)}/${year}`;
  };
  void datePattern;
  /** La fecha como la escribe la empresa: dd/mm/aaaa (es-PE) o mm/dd/aaaa (en-US); también sin ceros. */
  const parseTyped = (text: string): Day | null => {
    const match = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec(text);
    if (!match) return null;
    const [first, second, year] = [Number(match[1]), Number(match[2]), Number(match[3])];
    const [month, date] = locale === "en-US" ? [first, second] : [second, first];
    const twoDigits = match[1]!.length === 2 && match[2]!.length === 2;
    const oneDigit = match[1]!.length <= 2 && match[2]!.length <= 2;
    if (!twoDigits && !oneDigit) return null;
    const check = new Date(Date.UTC(year, month - 1, date));
    if (check.getUTCMonth() !== month - 1 || check.getUTCDate() !== date) return null;
    return dayOf(year, month, date);
  };
  const typeDate = (text: string, start: boolean) => {
    const trimmed = text.trim();
    const day = parseTyped(trimmed);
    const ok = day !== null && day <= today;
    if (start) {
      setFromText(ok ? null : trimmed);
      if (ok) setFrom(day);
    } else {
      setToText(ok ? null : trimmed);
      if (ok) setTo(day);
    }
    setError(ok ? null : ctl.L("INVALID_RANGE"));
    if (ok) setCalendarOpening((count) => count + 1);
  };
  const choosePeriod = (option: string) => {
    setPeriod(option);
    setFromText(null);
    setToText(null);
    if (option === "RANGE") {
      setFrom((current) => current ?? today);
      setTo((current) => current ?? today);
    }
  };
  const rangeShort = (start: Day, end: Day) => {
    const year = split(start)[0] !== split(end)[0];
    const short = (day: Day) => {
      const [y, m, d] = split(day);
      return (locale === "en-US" ? `${m}/${d}` : `${d}/${m}`) + (year ? `/${String(y % 100).padStart(2, "0")}` : "");
    };
    return `${short(start)} – ${short(end)}`;
  };
  const lowerFirst = (text: string) => (text ? text[0]!.toLowerCase() + text.slice(1) : text);
  const rangeLong = (start: Day, end: Day) => {
    const [fy, fm, fd] = split(start),
      [ty, tm, td] = split(end);
    if (locale === "en-US") {
      if (fy === ty && fm === tm) return `from ${fd} to ${td} of ${enMonths[tm - 1]} ${ty}`;
      return `from ${enMonths[fm - 1]} ${fd}, ${fy} to ${enMonths[tm - 1]} ${td}, ${ty}`;
    }
    if (fy === ty && fm === tm) return `del ${fd} al ${td} de ${esMonths[tm - 1]} de ${ty}`;
    return `del ${fd} de ${esMonths[fm - 1]} de ${fy} al ${td} de ${esMonths[tm - 1]} de ${ty}`;
  };
  const periodShort =
    value.period === "TODAY"
      ? ctl.L("TODAY")
      : value.period === "7D"
        ? ctl.L("7D")
        : value.period === "RANGE" && value.from && value.to
          ? rangeShort(value.from, value.to)
          : ctl.L(value.period);
  const dateTooltip = `${ctl.L("CREATION")}: ${
    value.period === "TODAY"
      ? lowerFirst(ctl.L("TODAY"))
      : value.period === "7D"
        ? lowerFirst(ctl.L("7D"))
        : value.period === "RANGE" && value.from && value.to
          ? rangeLong(value.from, value.to)
          : lowerFirst(ctl.L(value.period))
  }`;
  const agentName = (option: ContactOption) =>
    option.name ? option.name : ctl.L(option.kind === "AI" ? "AI_AGENT" : "NONE");
  /** Agentes a la vista: la elegida primero, luego las 5 con más conversaciones; buscando, solo coincidencias. */
  const visibleAgents = (() => {
    const all = page.agents;
    const key = (option: ContactOption) => option.kind + ":" + option.id;
    const byName = (a: ContactOption, b: ContactOption) =>
      agentName(a).localeCompare(agentName(b), undefined, { sensitivity: "base" });
    if (agentSearch.length > 0)
      return all
        .filter((option) => contains(agentName(option), agentSearch))
        .sort(
          (a, b) =>
            Number(key(b) === agent) - Number(key(a) === agent) ||
            b.conversations - a.conversations ||
            agentName(a).localeCompare(agentName(b)),
        );
    const chosen = all.filter((option) => key(option) === agent);
    const rest = all
      .filter((option) => key(option) !== agent)
      .sort((a, b) => b.conversations - a.conversations || byName(a, b));
    return agentExpanded ? [...chosen, ...rest] : [...chosen, ...rest.slice(0, 5)];
  })();
  /** C7-01: una píldora de etiqueta en la fila (la elegida o la más usada); las demás, en Más filtros. */
  const rowTags = (() => {
    const chosen = value.tags ?? [];
    const union = [...page.tags];
    for (const tag of chosen) if (!union.some((item) => lower(item) === lower(tag))) union.push(tag);
    const top = union
      .map((tag, index) => ({ tag, index }))
      .sort(
        (a, b) =>
          ctl.tagUsageOf(b.tag) - ctl.tagUsageOf(a.tag) ||
          a.tag.localeCompare(b.tag, locale, { sensitivity: "base" }) ||
          a.index - b.index,
      )
      .slice(0, rowTagLimit)
      .map((item) => item.tag);
    const row = [...chosen.filter((tag) => !top.some((item) => lower(item) === lower(tag))), ...top];
    const size = Math.max(rowTagLimit, chosen.length);
    for (let i = row.length - 1; row.length > size && i >= 0; i--)
      if (!chosen.some((tag) => lower(tag) === lower(row[i]!))) row.splice(i, 1);
    return row;
  })();
  const showTagSearch = page.tags.length > searchThreshold || tagSearch.length > 0;
  const showUnassigned = page.hasUnassigned && agentSearch.length === 0;
  const hiddenAgentCount =
    agentSearch.length > 0 || agentExpanded
      ? 0
      : Math.max(0, page.agents.filter((option) => option.kind + ":" + option.id !== agent).length - 5);

  const prepare = () => {
    setCalendarOpening((count) => count + 1);
    setDraftTags([...(value.tags ?? [])]);
    setDraftAny(value.anyTag);
    setTagSearch("");
    setAgentSearch("");
    setAgentExpanded(false);
    setOptions(page.tags);
    setTagError(null);
    setError(null);
    setInbox(value.inboxId !== null ? String(value.inboxId) : "");
    setAgent(
      value.agentKind === NONE ? NONE : value.agentUserId !== null ? value.agentKind + ":" + value.agentUserId : "",
    );
    setSelField(value.dateField);
    setPeriod(value.period);
    setFrom(value.from);
    setTo(value.to);
    setFromText(null);
    setToText(null);
  };
  const toggleQuick = (tag: string) => {
    const tags = [...(value.tags ?? [])];
    const index = tags.findIndex((item) => lower(item) === lower(tag));
    if (index >= 0) tags.splice(index, 1);
    else {
      if (tags.length >= 20) return;
      tags.push(tag);
    }
    void ctl.filter({ ...value, tags });
  };
  const chooseTag = (tag: string, selected: boolean) => {
    const has = draftTags.some((item) => lower(item) === lower(tag));
    if (!selected) {
      setDraftTags(draftTags.filter((item) => lower(item) !== lower(tag)));
      setTagError(null);
      return;
    }
    if (draftTags.length >= 20 && !has) {
      setTagError(ctl.L2("TAG_LIMIT"));
      return;
    }
    if (!has) setDraftTags([...draftTags, tag]);
    setTagError(null);
  };
  const searchTags = async (text: string) => {
    tagsPending.current?.abort();
    const controller = new AbortController();
    tagsPending.current = controller;
    const current = ++tagsRevision.current;
    if (text.length < 2) {
      setOptions(page.tags);
      setTagsMore(false);
      return;
    }
    try {
      await new Promise<void>((resolve, reject) => {
        const timer = setTimeout(resolve, 250);
        controller.signal.addEventListener("abort", () => {
          clearTimeout(timer);
          reject(new DOMException("cancelado", "AbortError"));
        });
      });
      const found = await ctl.api.tags(text, draftTags, controller.signal);
      if (current !== tagsRevision.current) return;
      setOptions(found.items);
      setTagsMore(found.hasMore);
    } catch (failure) {
      if (controller.signal.aborted) return;
      if (current === tagsRevision.current) setTagError(ctl.failure(failure));
    }
  };
  const clearTagSearch = async () => {
    setTagSearch("");
    const pendingSearch = searchTags("");
    document.getElementById("c1-filter-tag-search")?.focus();
    await pendingSearch;
  };
  const apply = async () => {
    if (period === "RANGE" && (!from || !to || from > to || fromText !== null || toText !== null)) {
      setError(ctl.L("INVALID_RANGE"));
      return;
    }
    panel.current?.close();
    const sorted = [...draftTags].sort((a, b) => (lower(a) < lower(b) ? -1 : lower(a) > lower(b) ? 1 : 0));
    const [kind, id] = agent.split(":", 2) as [string, string | undefined];
    const next: ContactQuery = {
      ...value,
      tags: sorted,
      anyTag: draftAny,
      page: 1,
      inboxId: /^\d+$/.test(inbox) ? Number(inbox) : null,
      agentUserId: agent === NONE ? null : id !== undefined && /^\d+$/.test(id) ? Number(id) : null,
      agentKind: agent === NONE ? NONE : id !== undefined ? kind : "HUMAN",
      dateField: selField,
      period,
      from,
      to,
    };
    await ctl.filter(next);
  };
  const tagOptions = [...options];
  for (const tag of draftTags) if (!tagOptions.some((item) => lower(item) === lower(tag))) tagOptions.push(tag);

  return (
    <div className="c1-filter-strip" role="group" aria-label={ctl.L("FILTERS")}>
      {(value.tags?.length ?? 0) >= 2 ? (
        <button
          type="button"
          className="chip c2-tag-chip active"
          data-tag={value.tags![0]}
          style={styleObject(tagStyle(ctl.tagColor(value.tags![0]!)))}
          aria-pressed="true"
          title={value.tags!.join(", ")}
          aria-label={value.tags!.join(", ")}
          onClick={() => panel.current?.open()}
        >
          <ChatIcon name="check" />
          <span>
            {value.tags![0]} +{value.tags!.length - 1}
          </span>
        </button>
      ) : (
        rowTags.map((tag) => {
          const chosen = value.tags?.includes(tag) === true;
          return (
            <button
              key={tag}
              type="button"
              className={`chip c2-tag-chip ${chosen ? "active" : ""} ${chosen && ctl.tagColorPending(tag) ? "c2-tag-pending" : ""}`}
              data-tag={tag}
              style={styleObject(tagStyle(ctl.tagColor(tag)))}
              aria-pressed={pressed(chosen)}
              onClick={() => toggleQuick(tag)}
            >
              <TagChipContent name={tag} chosen={chosen} />
            </button>
          );
        })
      )}
      {/* KH5 · C07: toda píldora aplicada es igual: encendida, con su ícono, y se quita al tocarla. */}
      {value.inboxId !== null ? (
        <button
          className="chip active"
          aria-pressed="true"
          onClick={() => void ctl.filter({ ...value, inboxId: null })}
        >
          <ChatIcon name="users" />
          <span>{page.inboxes.find((item) => item.id === value.inboxId)?.name}</span>
        </button>
      ) : null}
      {value.agentUserId !== null ? (
        <button
          className="chip active"
          aria-pressed="true"
          onClick={() => void ctl.filter({ ...value, agentUserId: null })}
        >
          <ChatIcon name="user" />
          <span>
            {page.agents.find((item) => item.id === value.agentUserId && item.kind === value.agentKind)?.name}
          </span>
        </button>
      ) : null}
      {value.agentKind === NONE ? (
        <button
          className="chip active"
          aria-pressed="true"
          onClick={() => void ctl.filter({ ...value, agentKind: "HUMAN" })}
        >
          <ChatIcon name="user" />
          <span>{ctl.L("UNASSIGNED")}</span>
        </button>
      ) : null}
      {value.period !== "ANY" ? (
        <button
          className="chip active c1-date-pill"
          aria-pressed="true"
          title={dateTooltip}
          aria-label={dateTooltip}
          onClick={() => void ctl.filter({ ...value, period: "ANY", from: null, to: null })}
        >
          <ChatIcon name="calendar" />
          <span>{periodShort}</span>
        </button>
      ) : null}
      <FilterPanel
        ref={panel}
        label={ctl.L("MORE_FILTERS")}
        clearLabel={ctl.L("CLEAR")}
        applyLabel={ctl.L("APPLY")}
        onPreparing={prepare}
        onApplying={apply}
        onClearing={() => void ctl.filter(clearedQuery(value))}
        trigger={
          <>
            <span>{ctl.L("MORE_FILTERS")}</span>
            <ChatIcon name="chevron-down" />
          </>
        }
      >
        <section className="c1-filter-group">
          <h3>{ctl.L("TAGS")}</h3>
          {/* C4-04 · M13: el buscador solo con más de 6 etiquetas; «Todas / Cualquiera» solo con 2 o más elegidas. */}
          {showTagSearch ? (
            <div className="c1-search">
              <ChatIcon name="search" />
              <input
                id="c1-filter-tag-search"
                type="search"
                enterKeyHint="search"
                aria-label={ctl.L2("SEARCH_TAGS")}
                placeholder={ctl.L2("SEARCH_TAGS")}
                value={tagSearch}
                onChange={(event) => {
                  setTagSearch(event.target.value);
                  void searchTags(event.target.value);
                }}
              />
              {tagSearch.length > 0 ? (
                <button
                  type="button"
                  className="c1-icon icon-button"
                  aria-label={ctl.L("CLEAR_SEARCH")}
                  onClick={() => void clearTagSearch()}
                >
                  <ChatIcon name="x" />
                </button>
              ) : null}
            </div>
          ) : null}
          <div className="c1-filter-options">
            {tagOptions
              .filter((tag) => contains(tag, tagSearch))
              .map((tag) => {
                const chosen = draftTags.some((item) => lower(item) === lower(tag));
                return (
                  <button
                    key={tag}
                    className={`chip c2-tag-chip ${chosen ? "active" : ""}`}
                    data-tag={tag}
                    style={styleObject(tagStyle(ctl.tagColor(tag)))}
                    type="button"
                    aria-pressed={pressed(chosen)}
                    onClick={() => chooseTag(tag, !chosen)}
                  >
                    <TagChipContent name={tag} chosen={chosen} />
                  </button>
                );
              })}
          </div>
          {draftTags.length >= 2 ? (
            <div className="c1-filter-options c1-tag-mode" role="group" aria-label={ctl.L("TAGS")}>
              {[false, true].map((any) => (
                <button
                  key={String(any)}
                  type="button"
                  className={`chip ${draftAny === any ? "active" : ""}`}
                  aria-pressed={pressed(draftAny === any)}
                  onClick={() => setDraftAny(any)}
                >
                  {ctl.L(any ? "ANY_TAGS" : "ALL_TAGS")}
                </button>
              ))}
            </div>
          ) : null}
          {tagsMore ? <p>{ctl.L2("TAG_SEARCH_MORE")}</p> : null}
          {tagError !== null ? <p role="status">{tagError}</p> : null}
        </section>
        <section className="c1-filter-group">
          <h3>{ctl.field("Inbox")}</h3>
          <div className="c1-filter-options" role="group" aria-label={ctl.field("Inbox")}>
            {page.inboxes.map((option) => {
              const id = String(option.id);
              const on = inbox === id;
              return (
                <button
                  key={id}
                  type="button"
                  className={`chip ${on ? "active" : ""}`}
                  aria-pressed={pressed(on)}
                  onClick={() => setInbox(on ? "" : id)}
                >
                  {option.name}
                </button>
              );
            })}
          </div>
          <h3>{ctl.field("Agent")}</h3>
          {page.agents.length > searchThreshold || agentSearch.length > 0 ? (
            <div className="c1-search">
              <ChatIcon name="search" />
              <input
                type="search"
                enterKeyHint="search"
                aria-label={ctl.L2("SEARCH_AGENTS")}
                placeholder={ctl.L2("SEARCH_AGENTS")}
                value={agentSearch}
                onChange={(event) => setAgentSearch(event.target.value)}
              />
              {agentSearch.length > 0 ? (
                <button
                  type="button"
                  className="c1-icon icon-button"
                  aria-label={ctl.L("CLEAR_SEARCH")}
                  onClick={() => setAgentSearch("")}
                >
                  <ChatIcon name="x" />
                </button>
              ) : null}
            </div>
          ) : null}
          <div className="c1-filter-options" role="group" aria-label={ctl.field("Agent")}>
            {visibleAgents.map((option) => {
              const key = option.kind + ":" + option.id;
              const on = agent === key;
              return (
                <button
                  key={key}
                  type="button"
                  className={`chip ${on ? "active" : ""}`}
                  aria-pressed={pressed(on)}
                  onClick={() => setAgent(on ? "" : key)}
                >
                  <ChatAvatar
                    name={agentName(option)}
                    identity={option.id}
                    agent={option.kind === "AI"}
                    className="c1-agent-avatar"
                  />
                  <span>
                    <Highlighted text={agentName(option)} term={agentSearch} />
                  </span>
                </button>
              );
            })}
            {showUnassigned ? (
              <button
                type="button"
                className={`chip ${agent === NONE ? "active" : ""}`}
                aria-pressed={pressed(agent === NONE)}
                onClick={() => setAgent(agent === NONE ? "" : NONE)}
              >
                <ChatIcon name="circle-dashed" />
                <span>{ctl.L("UNASSIGNED")}</span>
              </button>
            ) : null}
            {!agentExpanded && hiddenAgentCount > 0 ? (
              <button type="button" className="chip" onClick={() => setAgentExpanded(true)}>
                {ctl.L2("VIEW_AGENTS").replace("{0}", String(page.agents.length))}
              </button>
            ) : null}
          </div>
        </section>
        {/* D1: la única fecha es la de creación del contacto; el grupo dice cuál es y el período, cuándo. */}
        <section className="c1-filter-group">
          <h3>{ctl.L("CREATION")}</h3>
          <div className="c1-filter-options" role="group" aria-label={ctl.L("CREATION")}>
            {["ANY", "TODAY", "7D", "RANGE"].map((option) => (
              <button
                key={option}
                type="button"
                className={`chip ${period === option ? "active" : ""}`}
                aria-pressed={pressed(period === option)}
                onClick={() => choosePeriod(option)}
              >
                {ctl.L(option === "ANY" ? "ANY_DATE" : option === "RANGE" ? "CHOOSE_DATES" : option)}
              </button>
            ))}
          </div>
          {/* C9-04 · C07 1.12: con «Elegir fechas», Desde y Hasta y un mes entero se abren aquí, debajo de sus píldoras. */}
          {period === "RANGE" ? (
            <div className="c1-range" role="group" aria-label={ctl.L("CHOOSE_DATES")}>
              <div className="c1-range-fields">
                <label className="field" htmlFor="c1-from">
                  <span className="c1-field-label">{ctl.L("FROM")}</span>
                  <DateInput
                    id="c1-from"
                    placeholder={show(today)}
                    value={fromText ?? show(from)}
                    invalid={fromText !== null}
                    onCommit={(text) => typeDate(text, true)}
                  />
                </label>
                <label className="field" htmlFor="c1-to">
                  <span className="c1-field-label">{ctl.L("TO")}</span>
                  <DateInput
                    id="c1-to"
                    placeholder={show(today)}
                    value={toText ?? show(to)}
                    invalid={toText !== null}
                    onCommit={(text) => typeDate(text, false)}
                  />
                </label>
              </div>
              <div className="c1-range-calendar">
                <RangeCalendar
                  key={calendarOpening}
                  inline
                  maxDays={null}
                  language={locale === "en-US" ? "en-US" : "es-PE"}
                  from={from ?? today}
                  to={to ?? today}
                  today={today}
                  previousLabel={ctl.L("PREVIOUS")}
                  nextLabel={ctl.L("NEXT")}
                  dayOneLabel={ctl.L("DAY_ONE")}
                  daysManyLabel={ctl.L("DAYS_MANY")}
                  previousArrow={<ChatIcon name="chevron-left" />}
                  nextArrow={<ChatIcon name="chevron-right" />}
                  onChanged={(start, end) => {
                    setFrom(start);
                    setTo(end);
                    setFromText(null);
                    setToText(null);
                    setError(null);
                  }}
                />
              </div>
              <FieldError id="c1-range-error" message={error} />
            </div>
          ) : null}
        </section>
      </FilterPanel>
    </div>
  );
}

/** Campo de fecha escrita: como @onchange de Blazor, se confirma al salir del campo o con Intro. */
export function DateInput({
  id,
  placeholder,
  value,
  invalid,
  onCommit,
  errorId = "c1-range-error",
}: {
  id: string;
  placeholder: string;
  value: string;
  invalid: boolean;
  onCommit: (text: string) => void;
  errorId?: string;
}) {
  const [text, setText] = useState(value);
  const [seen, setSeen] = useState(value);
  if (seen !== value) {
    setSeen(value);
    setText(value);
  }
  return (
    <input
      id={id}
      inputMode="numeric"
      autoComplete="off"
      placeholder={placeholder}
      value={text}
      aria-invalid={invalid ? "true" : undefined}
      aria-describedby={errorId}
      onChange={(event) => setText(event.target.value)}
      onBlur={(event) => {
        if (event.target.value !== value) onCommit(event.target.value);
      }}
      onKeyDown={(event) => {
        if (event.key === "Enter" && event.currentTarget.value !== value) onCommit(event.currentTarget.value);
      }}
    />
  );
}
